import { Link } from './Link';
import { Icon } from './icons';
import { useBillingMode } from '../lib/useBillingMode';
import { useFeatures, type FeatureKind } from '../lib/useFeatures';
import { ScrollStory, StageCanvas, StageCard, StagePanels, type StoryBeat } from './ScrollStory';
import type { CSSProperties } from 'react';

/**
 * Homepage "how it works" — a scroll-driven walk from sign-up to first verdict across six beats:
 * workspace + trial → API key → POST /v1/emails and the verdict back → webhook subscriptions →
 * alert channels → the portal. The scroll choreography + responsive layout live in <ScrollStory>;
 * this file supplies the beat copy and the "wiring stage" graphic (your app ↔ /v1 API).
 *
 * Everything shown is the ACTUAL product — endpoints, payload fields, webhook event names and
 * portal pages. The only DYNAMIC bits (trial length + starting credits) come from the super-admin
 * config via useBillingMode().trial, which the SSG prefetch bakes into the prerendered HTML — so
 * the copy states the real offer, never a hard-coded number that drifts from production.
 */

const FEATURE_LINKS: Array<{ kind: FeatureKind | 'core'; slug: string; title: string; blurb: string }> = [
  { kind: 'core', slug: 'bot-detection', title: 'Bot detection & verification', blurb: 'Clustering, scoring and the mailbox check — the one call every signup runs.' },
  { kind: 'domain_health', slug: 'domain-health', title: 'Domain health', blurb: 'SPF / DKIM / DMARC / MX scored on every sending domain.' },
  { kind: 'inbox_placement', slug: 'inbox-placement', title: 'Inbox placement', blurb: 'Seed tests showing inbox vs spam vs missing, per ESP.' },
  { kind: 'warmup_inbox', slug: 'warmup', title: 'Warmup', blurb: 'Auto-warm sending mailboxes to build ESP reputation.' },
  { kind: 'phone_validation', slug: 'phone-validation', title: 'Phone validation', blurb: 'Carrier, line type and reachability at signup.' },
  { kind: 'blacklist', slug: 'blacklist-checks', title: 'Blacklist checks', blurb: '90+ DNSBLs watched on every domain you ingest.' },
];

type Beat = StoryBeat & { detail: React.ReactNode };

export function HowItWorksStory() {
  const billing = useBillingMode();
  const { feature } = useFeatures();
  const days = billing.trial.days;
  const credits = billing.trial.credits.toLocaleString();

  const beats: Beat[] = [
    {
      n: 1,
      kicker: 'Set up',
      title: 'Spin up a workspace',
      body: (
        <>
          Register and your workspace starts on a <b className="font-semibold text-txt">{days}-day trial</b> with{' '}
          <b className="font-semibold text-txt">{credits} shared credits</b> — no card. Credits are spent as you go
          across detection, verification, blacklist, phone and domain health.
        </>
      ),
      facts: ['POST /auth/register', `${days}-day trial`, `${credits} credits`],
      detail: (
        <StageCard head="Workspace created">
          <div className="flex items-center gap-2 text-[15px] font-semibold">
            acme
            <span className="rounded-full bg-allow/15 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-allow">
              Trial
            </span>
          </div>
          <div className="mt-1.5 font-mono text-[11px] text-faint md:text-[11.5px]">
            detection · verification · blacklist · phone · domain health
          </div>
        </StageCard>
      ),
    },
    {
      n: 2,
      kicker: 'Connect',
      title: 'Mint an API key',
      body: (
        <>
          Create a key under <span className="font-mono">API keys</span>. You see the full secret exactly once — we
          store only its hash. Keys start with <span className="font-mono text-accent">sk_</span> and can carry an
          expiry.
        </>
      ),
      facts: ['POST /api-keys', 'shown once', 'stored hashed'],
      detail: (
        <StageCard head="Secret — copy it now">
          <div className="font-mono text-[13.5px] md:text-[15px]">
            <span className="text-accent">sk_a1b2c3d4e5f6</span>
            <span className="text-faint">.7f3e…9c02b1a4d5</span>
          </div>
          <div className="mt-1.5 font-mono text-[11px] text-faint">stored as sha-256 · never shown again</div>
        </StageCard>
      ),
    },
    {
      n: 3,
      kicker: 'Send & receive',
      title: 'One POST, a verdict back',
      body: (
        <>
          Send up to 1,000 addresses to <span className="font-mono">/v1/emails</span>. Each returns an action —{' '}
          <span className="font-mono text-allow">allow</span>, <span className="font-mono text-verify">verify</span> or{' '}
          <span className="font-mono text-review">review</span> — with a score, the reasons, its duplicate cluster and a
          deliverability verdict.
        </>
      ),
      facts: ['action', 'score + reasons', 'cluster + verification'],
      detail: (
        <>
          <StageCard head="POST /v1/emails">
            <Code>{`{ "emails": ["ada@stripe.com", "ada+01@stripe.com"],
  "listId": "signups" }`}</Code>
          </StageCard>
          <StageCard head="200 · verdict review">
            <Code>{`{ "email": "ada+01@stripe.com", "action": "review",
  "score": 72, "reasons": ["near_duplicates"],
  "cluster": { "id": "clu_8f", "size": 6 },
  "verification": { "status": "risky" } }`}</Code>
          </StageCard>
        </>
      ),
    },
    {
      n: 4,
      kicker: 'Subscribe',
      title: 'Get pushed what matters',
      body: (
        <>
          Register an endpoint and choose events. Every delivery is signed with{' '}
          <span className="font-mono">X-Sieve-Signature</span> (HMAC-SHA256) and retried with backoff — so you can trust
          it and replay it.
        </>
      ),
      facts: ['POST /webhooks/endpoints', 'signed', 'auto-retry'],
      detail: (
        <StageCard head="Subscribe to events">
          <Evt code="emails.scored" desc="a batch finished screening" />
          <Evt code="email.duplicate_detected" desc="an alias folded into an existing signer" />
          <Evt code="domain.blacklisted" desc="a clean sending domain hit a DNSBL" />
          <Evt code="quota.threshold_crossed" desc="you're at 80% of an allowance" />
        </StageCard>
      ),
    },
    {
      n: 5,
      kicker: 'Stay in the loop',
      title: 'Be told, your way',
      body: (
        <>
          Point events at Slack, email or Intercom per channel, filtered by disposition. Plus a Monday AI digest per
          workspace — detection, domain health and warmup in one email.
        </>
      ),
      facts: ['Slack', 'Email', 'Intercom', 'Weekly digest'],
      detail: (
        <StageCard head="Route alerts your way">
          <div className="grid grid-cols-2 gap-2">
            <Alert glyph="#" tone="allow" title="Slack" sub="review queue" />
            <Alert glyph="@" tone="verify" title="Email" sub="per disposition" />
            <Alert glyph="i" tone="accent" title="Intercom" sub="in-product" />
            <Alert glyph="✦" tone="review" title="Weekly digest" sub="Monday · AI" />
          </div>
        </StageCard>
      ),
    },
    {
      n: 6,
      kicker: 'Operate',
      title: 'Or drive it from the console',
      body: (
        <>
          Everything the API does has a home in the portal: submit and score in the Console, clear flagged sign-ups in
          the Review queue, dig through Records and Clusters, and watch Analytics, Domain health and API activity.
        </>
      ),
      facts: ['Console', 'Review queue', 'Analytics'],
      detail: <PortalFrame />,
    },
  ];

  const featureLinks = FEATURE_LINKS.filter((f) => f.kind === 'core' || feature(f.kind as FeatureKind));
  const panels = beats.map((b) => ({ n: b.n, content: b.detail }));

  // sg-defer: by far the tallest thing on the homepage (~4300px on mobile), so it is where render
  // containment pays most — see .sg-defer in index.css. The scroll choreography is unaffected: the
  // IntersectionObserver and scroll listener in <ScrollStory> only start mattering once the section
  // is near the viewport, which is exactly when the browser stops skipping it.
  return (
    <section
      className="sg-defer mt-24"
      style={{ '--sg-defer-h': '4307px', '--sg-defer-h-md': '3925px' } as CSSProperties}
      aria-label="How SieveGuard works"
    >
      <div className="text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
          <span className="h-1.5 w-1.5 rounded-full bg-accent" /> How it works
        </span>
        <h2 className="mt-5 font-display text-3xl font-bold tracking-tight md:text-4xl">
          From sign-up to first verdict
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-[14.5px] leading-relaxed text-muted">
          No black box. Follow the real path a team takes to wire up SieveGuard — every endpoint, payload and event below
          is exactly what you&apos;ll use.
        </p>
      </div>

      <ScrollStory
        beats={beats}
        renderStage={(beat, p) => (
          <StageCanvas>
            <Node side="left" label="Your app" value="signups.acme.io" lit={beat >= 1} accent="allow" />
            <Node side="right" label="SieveGuard" value="/v1 API" lit={beat >= 2} accent="accent" />
            <svg
              className="absolute inset-x-0 top-[52px] h-8 w-full md:top-[70px] md:h-10"
              viewBox="0 0 600 40"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path d="M 96 20 H 504" fill="none" stroke="var(--color-border)" strokeWidth={2} strokeDasharray="3 5" />
              <path
                d="M 96 20 H 504"
                fill="none"
                stroke="var(--color-accent)"
                strokeWidth={2}
                strokeLinecap="round"
                pathLength={1}
                strokeDasharray={1}
                style={{ strokeDashoffset: 1 - p, filter: 'drop-shadow(0 0 5px color-mix(in srgb, var(--color-accent) 55%, transparent))' }}
              />
            </svg>
            {beat === 3 && (
              <>
                {/* Each packet is a zero-cost TRACK (left 14%, width 70% — the exact travel
                    distance) with the visible dot pinned at its left edge. The track is what
                    animates, by translateX(0 → 100%) of ITS OWN width, which lands the dot on
                    14% → 84%. That indirection is the point: a transform percentage resolves
                    against the transformed element, so animating the 8px dot directly could not
                    express "70% of the stage" — which is why this used to animate `left` and
                    re-laid-out the page on every frame. See .sg-hiw-req in index.css. */}
                <span className="sg-hiw-req pointer-events-none absolute top-[50px] left-[14%] w-[70%] md:top-[66px]">
                  <span className="block h-2 w-2 rounded-full bg-accent" />
                </span>
                <span className="sg-hiw-res pointer-events-none absolute top-[58px] left-[14%] w-[70%] md:top-[74px]">
                  <span className="block h-2 w-2 rounded-full bg-allow" />
                </span>
              </>
            )}
            <StagePanels beat={beat} panels={panels} />
          </StageCanvas>
        )}
      />

      {/* ── Go deeper: each feature's own how-it-works ──────────────────────────────────── */}
      <div className="mt-12 rounded-2xl border border-bordersoft bg-panel p-6 md:mt-16 md:p-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-bold tracking-tight">Go deeper, feature by feature</h3>
            <p className="mt-1 text-[13.5px] text-muted">
              Each feature has its own step-by-step story — what it does under the hood, and the result you&apos;re after.
            </p>
          </div>
          <Link to="/features" className="font-mono text-[12px] text-accent hover:underline">
            Browse all features →
          </Link>
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {featureLinks.map((f) => (
            <Link
              key={f.slug}
              to={`/features/${f.slug}`}
              className="group rounded-xl border border-border bg-bg/40 p-4 transition hover:-translate-y-0.5 hover:border-accentdeep/60"
            >
              <div className="flex items-center justify-between">
                <span className="text-[14px] font-semibold">{f.title}</span>
                <span className="text-accent transition group-hover:translate-x-0.5">
                  <Icon.ArrowRight width={15} height={15} />
                </span>
              </div>
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted">{f.blurb}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function PortalFrame() {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-panel">
      <div className="flex items-center gap-1.5 border-b border-bordersoft bg-panel2 px-3 py-2.5">
        <i className="h-2 w-2 rounded-full bg-border" />
        <i className="h-2 w-2 rounded-full bg-border" />
        <i className="h-2 w-2 rounded-full bg-border" />
        <span className="ml-2 font-mono text-[11px] text-faint">app.sieveguard.com/app</span>
      </div>
      <div className="flex flex-wrap gap-1.5 p-3">
        {['Console', 'Review queue', 'Records', 'Clusters', 'Analytics', 'Domain health', 'Webhooks', 'API activity'].map(
          (t, i) => (
            <span
              key={t}
              className={`rounded-md border px-2 py-1.5 font-mono text-[11.5px] ${
                i === 0 ? 'border-accent bg-accent font-bold text-bg' : 'border-border bg-bg text-muted'
              }`}
            >
              {t}
            </span>
          ),
        )}
      </div>
    </div>
  );
}

function Node({
  side,
  label,
  value,
  lit,
  accent,
}: {
  side: 'left' | 'right';
  label: string;
  value: string;
  lit: boolean;
  accent: 'allow' | 'accent';
}) {
  const led = lit
    ? accent === 'allow'
      ? 'bg-allow shadow-[0_0_10px_var(--color-allow)]'
      : 'bg-accent shadow-[0_0_10px_var(--color-accent)]'
    : 'bg-muted';
  return (
    <div
      className={`absolute top-4 w-[132px] rounded-lg border border-border bg-panel2/90 px-2.5 py-2 backdrop-blur md:top-[34px] md:w-[168px] md:rounded-xl md:px-3.5 md:py-3 ${
        side === 'left' ? 'left-3 md:left-[26px]' : 'right-3 text-right md:right-[26px]'
      }`}
    >
      <div className="font-mono text-[9px] uppercase tracking-[0.12em] text-faint md:text-[10px] md:tracking-[0.14em]">
        {label}
      </div>
      <div
        className={`mt-0.5 flex items-center gap-1.5 text-[12px] font-semibold md:mt-1 md:gap-2 md:text-[13.5px] ${
          side === 'right' ? 'justify-end' : ''
        }`}
      >
        {side === 'left' && <span className={`h-2 w-2 shrink-0 rounded-full ${led} transition-all duration-500`} />}
        <span className="truncate">{value}</span>
        {side === 'right' && <span className={`h-2 w-2 shrink-0 rounded-full ${led} transition-all duration-500`} />}
      </div>
    </div>
  );
}

function Code({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto font-mono text-[10.5px] leading-relaxed text-txt md:text-[12px]">
      <code>{children}</code>
    </pre>
  );
}

function Evt({ code, desc }: { code: string; desc: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 border-t border-bordersoft py-1.5 first:border-t-0">
      <code className="whitespace-nowrap font-mono text-[11.5px] text-accent md:text-[12px]">{code}</code>
      <span className="text-[12px] text-muted md:text-[12.5px]">{desc}</span>
    </div>
  );
}

function Alert({ glyph, tone, title, sub }: { glyph: string; tone: 'allow' | 'verify' | 'accent' | 'review'; title: string; sub: string }) {
  const bg = { allow: 'bg-allow', verify: 'bg-verify', accent: 'bg-accent', review: 'bg-review' }[tone];
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-panel px-2.5 py-2 md:gap-2.5 md:px-3 md:py-2.5">
      <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-md font-mono text-[12px] font-bold text-bg ${bg}`}>
        {glyph}
      </span>
      <span className="text-[12.5px] font-semibold leading-tight md:text-[13px]">
        {title}
        <small className="block font-mono text-[10.5px] font-normal text-faint md:text-[11px]">{sub}</small>
      </span>
    </div>
  );
}
